import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, MapPin, Sparkles } from "lucide-react";
import { scrollToSection } from "@/lib/api";

const HEADLINE = ["Daha Fazla", "Müşteriye Ulaş,", "YıkaGel Partneri Ol"];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 + i * 0.12 },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBack = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yFront = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="hero" ref={ref} className="relative overflow-hidden pt-28 pb-16 sm:pt-36 sm:pb-24">
      <div className="hero-glow absolute inset-0 -z-10" />
      <div className="absolute -z-10 top-24 -right-24 h-[420px] w-[420px] rounded-full bg-[#22C7D6]/10 blur-3xl" />

      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
        {/* Copy */}
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#1677FF]/20 bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#1677FF]"
          >
            <Sparkles size={14} /> Partner Programı
          </motion.div>

          <h1 className="mt-6 font-heading text-[2.6rem] leading-[0.98] sm:text-6xl xl:text-7xl font-black tracking-tighter text-[#172033]">
            {HEADLINE.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-6 max-w-xl text-lg leading-relaxed text-[#667085]"
          >
            YıkaGel ile bulunduğun bölgedeki araç sahiplerinden yeni hizmet talepleri al,
            teklifini gönder ve işini büyüt.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
          >
            <button
              data-testid="hero-cta"
              onClick={() => scrollToSection("basvuru")}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#1677FF] px-7 py-4 text-base font-semibold text-white shadow-[0_14px_30px_-10px_rgba(22,119,255,0.7)] hover:bg-[#0B3B75] transition-colors"
            >
              Partner Başvurusu Yap
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-secondary"
              onClick={() => scrollToSection("nasil-calisir")}
              className="inline-flex items-center justify-center rounded-full border border-gray-200 bg-white px-7 py-4 text-base font-semibold text-[#172033] hover:border-[#1677FF] hover:text-[#1677FF] transition-colors"
            >
              Nasıl Çalışır?
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="mt-5 flex items-center gap-2 text-sm text-[#667085]"
          >
            <ShieldCheck size={16} className="text-[#22C7D6]" />
            Başvuru ücretsizdir. Ekibimiz başvurunuzu inceleyerek sizinle iletişime geçer.
          </motion.p>
        </div>

        {/* Visual */}
        <div className="lg:col-span-6 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.35, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <motion.div style={{ y: yBack }} className="relative overflow-hidden rounded-[2rem] shadow-[0_40px_80px_-30px_rgba(11,59,117,0.45)]">
              <img
                src="https://images.unsplash.com/photo-1565689876697-e467b6c54da2?auto=format&fit=crop&w=1000&q=80"
                alt="Köpük ile araç yıkama"
                className="h-[440px] sm:h-[540px] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B3B75]/30 to-transparent" />
            </motion.div>

            <motion.div
              style={{ y: yFront }}
              className="absolute -left-4 sm:-left-10 bottom-10 w-44 sm:w-56 overflow-hidden rounded-2xl border-4 border-white shadow-2xl"
            >
              <img
                src="https://images.unsplash.com/photo-1694678505383-676d78ea3b96?auto=format&fit=crop&w=500&q=80"
                alt="Yerinde araç yıkama hizmeti"
                className="h-40 sm:h-48 w-full object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.7 }}
              className="absolute -right-2 sm:right-6 top-8 flex items-center gap-3 rounded-2xl bg-white/90 backdrop-blur px-4 py-3 shadow-xl border border-white"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#22C7D6]/15 text-[#0B3B75]">
                <MapPin size={18} />
              </span>
              <div>
                <p className="text-xs text-[#667085]">Bölgende</p>
                <p className="text-sm font-bold text-[#172033]">Yeni Talepler</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
